import {
	Box,
	Spinner,
	Money,
	Button,
	Stack,
	Inline,
	Icons,
	Heading,
	Text,
	Tabs,
	TabsList,
	TabItem,
	TabContent,
	Component,
	Tooltip,
} from "@sembark-travel/ui-base"
import { XHRInstance, useXHR } from "@sembark-travel/xhr"
import { type ITrip, tripInvoicesXHR, TTripInvoice } from "./store"
import { createMoney } from "@sembark-travel/money"
import { Required } from "utility-types"
import useSWR from "swr"
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import {
	Dialog,
	useDialog,
	ConfirmationDialog,
	ConfirmationDialogProvider,
} from "@sembark-travel/ui-dialog"
import config from "../config"
import { Pick } from "@react-spring/web"
import {
	InvoiceItemDetails,
	InvoiceForm,
	InvoiceFormData,
	TTemplateProformaInvoiceData,
	TInvoiceFilter,
	TInvoiceCustomFilter,
} from "../Invoices"
import { UpdateTourist } from "../Tourists"
import { EditTripSourceItem } from "../TripSources"
import { IAddress } from "../Addresses"

export function TripInvoices({
	trip,
	onChange: revalidateTrip,
}: {
	trip: ITrip
	onChange: () => void
}) {
	const xhr = useXHR()
	const [selectedInvoice, setSelectedInvoice] = useState<
		TTripInvoice | undefined
	>(undefined)
	const { data, mutate: revalidate } = useSWR(
		`/trips/${trip.id}/invoices`,
		() => tripInvoicesXHR(xhr).get(trip.id)
	)
	useEffect(() => {
		if (!selectedInvoice && data?.data?.length) {
			setSelectedInvoice(data.data[0])
		}
	}, [data, selectedInvoice])
	const [
		isOpenCreateInvoiceDialog,
		openCreateInvoiceDialog,
		closeCreateInvoiceDialog,
	] = useDialog()
	if (!data) {
		return <Spinner padding="4" alignCenter />
	}
	const invoices = data.data
	const canCreate = true

	return (
		<>
			<Stack gap="4">
				<Box padding="4" bgColor="default">
					<Inline justifyContent="between" gap="4" alignItems="center">
						<Heading>Proforma Invoice</Heading>
						{canCreate ? (
							<Tooltip
								disabled={invoices.length < 15}
								content={
									"Can not create more than 15 invoices. Please delete any existing invoice to create a new one."
								}
							>
								<Box>
									<Button
										onClick={() => openCreateInvoiceDialog()}
										size="sm"
										level="tertiary"
										disabled={invoices.length >= 15 ? true : false}
									>
										<Icons.Plus /> New
									</Button>
								</Box>
							</Tooltip>
						) : null}
					</Inline>
				</Box>
				{!invoices.length ? (
					<Box padding="4" textAlign="center">
						<Stack gap="4">
							<Text fontSize="lg">
								No Proforma Invoice created for this Trip!
							</Text>
							{canCreate ? (
								<Box>
									<Button onClick={() => openCreateInvoiceDialog()}>
										Create Proforma Invoice
									</Button>
								</Box>
							) : null}
						</Stack>
					</Box>
				) : (
					<Tabs layout="col">
						{invoices.length > 1 ? (
							<TabsList>
								{invoices.map((i) => (
									<TabItem
										key={i.id}
										onClick={() => setSelectedInvoice(i)}
										active={selectedInvoice?.id === i.id}
									>
										<Stack gap="1">
											<Money
												money={createMoney(i.total_amount, i.currency)}
												showCurrency
												fontSize="md"
											/>
										</Stack>
									</TabItem>
								))}
							</TabsList>
						) : null}
						<TabContent>
							{!selectedInvoice ? (
								<Spinner padding="4" alignCenter />
							) : (
								<InvoiceDetails
									invoice={selectedInvoice}
									trip={trip}
									canChange={canCreate}
									revalidateTrip={revalidateTrip}
									onChange={(invoice) => {
										setSelectedInvoice(invoice)
										revalidate()
									}}
									onDelete={async () => {
										await revalidate()
										setSelectedInvoice(undefined)
									}}
								/>
							)}
						</TabContent>
					</Tabs>
				)}
			</Stack>
			<Dialog
				open={isOpenCreateInvoiceDialog}
				onClose={closeCreateInvoiceDialog}
				title={`Create New Proforma Invoice`}
				fitContainer
			>
				<Dialog.Body>
					<CreateInvoice
						trip={trip}
						revalidateTrip={revalidateTrip}
						onSuccess={(invoice) => {
							setSelectedInvoice(invoice)
							revalidate()
							closeCreateInvoiceDialog()
						}}
						onCancel={closeCreateInvoiceDialog}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function InvoiceDetails({
	trip,
	revalidateTrip,
	...props
}: React.ComponentProps<typeof InvoiceItemDetails> & {
	trip: Pick<ITrip, "id" | "trip_source" | "tourist" | "package_amount">
	revalidateTrip: () => void
}) {
	return (
		<InvoiceItemDetails
			{...props}
			actions={({ refresh }) => (
				<Component initialState={false}>
					{({ state, setState }) => (
						<>
							<Button size="sm" status="primary" onClick={() => setState(true)}>
								<Icons.Pencil /> Edit
							</Button>
							<Dialog
								open={state}
								onClose={() => setState(false)}
								title={`Edit Proforma Invoice`}
								fitContainer
							>
								<Dialog.Body>
									<EditInvoice
										invoice={props.invoice}
										trip={trip}
										revalidateTrip={revalidateTrip}
										onSuccess={(invoice) => {
											props.onChange(invoice)
											setState(false)
											refresh()
										}}
										onCancel={() => setState(false)}
									/>
								</Dialog.Body>
							</Dialog>
						</>
					)}
				</Component>
			)}
		/>
	)
}

function CreateInvoice({
	trip,
	...props
}: Omit<
	React.ComponentProps<typeof TripInvoiceForm>,
	| "initialValues"
	| "onSubmit"
	| "currency"
	| "fetchPayload"
	| "sellerFunctionalCurrency"
	| "filters"
	| "setFilters"
	| "hideTaxBreakup"
	| "setHideTaxBreakup"
>) {
	const xhr = useXHR()
	const [filters, setFilters] = useState<Array<TInvoiceFilter>>([])
	const [hideTaxBreakup, setHideTaxBreakup] = useState<0 | 1>(0)

	return (
		<TripInvoiceForm
			trip={trip}
			{...props}
			filters={filters}
			setFilters={setFilters}
			hideTaxBreakup={hideTaxBreakup}
			setHideTaxBreakup={setHideTaxBreakup}
			fetchPayload={(xhr, params) =>
				xhr
					.get<{
						data: Required<
							InvoiceFormData & TTemplateProformaInvoiceData,
							"currency" | "items" | "total_amount"
						>
						meta: {
							options: Array<TInvoiceFilter>
						}
					}>(`/trips/${trip.id}/invoices/create`, {
						params,
					})
					.then((resp) => ({
						...resp.data.data,
						options: resp.data.meta?.options,
					}))
			}
			onSubmit={async (values) => {
				const { items, ...rest } = values
				// Transform items to set editing_amount flag on each tax item as backend api needs this data at each tax level for each item,
				// But our InvoiceForm maintains "tax_applied_on_percentage" flag at root of each item as single source of truth for all taxes in a single item, as this setting is global for all taxes inside an item
				// which can be toggle from the tax table header inside an item.
				const transformedItems = items?.map((item) => {
					const { tax_applied_on_percentage, ...rest } = item
					const transformedTaxes = item.taxes?.map((tax) => ({
						...tax,
						// Converting tax_applied_on_percentage to editing_amount flag
						editing_amount: (tax_applied_on_percentage !== 0 ? 0 : 1) as 0 | 1,
					}))

					return {
						...rest,
						taxes: transformedTaxes,
					}
				})

				const hideItemizedBreakup = filters.find(
					(f) => f.id === "hide_itemized_breakup"
				)?.included

				const payload = {
					...rest,
					items: transformedItems,
					flags: {
						hide_itemized_breakup: hideItemizedBreakup ? 1 : 0,
					},
				}
				const invoice = await tripInvoicesXHR(xhr).store(trip.id, payload)
				return invoice
			}}
		/>
	)
}

function EditInvoice({
	invoice,
	...props
}: Omit<
	React.ComponentProps<typeof TripInvoiceForm>,
	| "initialValues"
	| "onSubmit"
	| "currency"
	| "fetchPayload"
	| "filters"
	| "setFilters"
	| "hideTaxBreakup"
	| "setHideTaxBreakup"
> & {
	invoice: TTripInvoice
}) {
	const xhr = useXHR()

	return (
		<TripInvoiceForm
			{...props}
			fetchPayload={(xhr, params) =>
				xhr
					.get<{
						data: Required<
							InvoiceFormData & TTemplateProformaInvoiceData,
							"currency" | "items" | "total_amount"
						>
					}>(`/invoices/${invoice.id}/edit`, { params })
					.then((resp) => ({
						...resp.data.data,
					}))
			}
			onSubmit={async (values) => {
				const { items, ...rest } = values
				// Transform items to set editing_amount flag on each tax item as backend api needs this data at each tax level for each item,
				// But our InvoiceForm maintains "tax_applied_on_percentage" flag at root of each item as single source of truth for all taxes in a single item, as this setting is global for all taxes inside an item
				// which can be toggle from the tax table header inside an item.
				const transformedItems = items?.map((item) => {
					const { tax_applied_on_percentage, ...rest } = item
					const transformedTaxes = item.taxes?.map((tax) => ({
						...tax,
						// Convert tax_applied_on_percentage to editing_amount flag
						editing_amount: (tax_applied_on_percentage !== 0 ? 0 : 1) as 0 | 1,
					}))

					return {
						...rest,
						taxes: transformedTaxes,
					}
				})

				const payload = {
					items: transformedItems,
					...rest,
				}
				return await tripInvoicesXHR(xhr).update(
					props.trip.id,
					invoice.id,
					payload
				)
			}}
		/>
	)
}

/**
 * It renders both dynamic and custom filter options for invoice forms
 */
export function InvoiceFormFilters({
	filters,
	onFiltersChange,
	customFilters,
	disabled = false,
}: {
	filters?: Array<TInvoiceFilter>
	onFiltersChange?: (filters: Array<TInvoiceFilter>) => void
	customFilters?: Array<TInvoiceCustomFilter>
	disabled?: boolean
}) {
	const hasAnyFilters =
		(filters && filters.length > 0) ||
		(customFilters && customFilters.length > 0)

	if (!hasAnyFilters) {
		return null
	}

	return (
		<Box padding="4" bgColor="inset" borderWidth="1" rounded="lg">
			<Stack gap="3">
				<Heading fontSize="sm" color="muted">
					Invoice Options
				</Heading>
				<Inline gap="4" flexWrap="wrap">
					{/* Custom Filters */}

					{customFilters?.map((customFilter) => {
						const filterId = customFilter.name.replace(/\s/g, "_").toLowerCase()

						return (
							<Inline key={customFilter.name} gap="2" alignItems="center">
								<input
									type="checkbox"
									checked={Boolean(customFilter.included)}
									id={`${filterId}_checkbox`}
									disabled={customFilter.disabled || disabled}
									onChange={() =>
										customFilter.onChange(customFilter.included ? 0 : 1)
									}
								/>
								<label htmlFor={`${filterId}_checkbox`}>
									{customFilter.name}
								</label>
							</Inline>
						)
					})}

					{/* Dynamic API-driven Filters */}

					{filters?.map((filter, index) => {
						const filterId = filter.name.replace(/\s/g, "_").toLowerCase()

						return (
							<Inline key={filter.name} gap="2" alignItems="center">
								<input
									type="checkbox"
									checked={Boolean(filter.included)}
									id={`${filterId}_checkbox`}
									disabled={filter.disabled || disabled}
									onChange={() =>
										onFiltersChange?.(
											filters.map((f, i) => ({
												...f,
												included:
													i === index
														? ((f.included ? 0 : 1) as 0 | 1)
														: f.included,
											}))
										)
									}
								/>
								{filter.options && filter.options.length > 1 ? (
									<Box position="relative">
										<Box as="label" htmlFor={`${filterId}_select_option`}>
											{filter.name} <Icons.ChevronDown size="3" />
										</Box>
										<Box
											position="absolute"
											overflow="hidden"
											inset="0"
											title="Select more options"
										>
											<select
												value={filter.name}
												id={`${filterId}_select_option`}
												style={{
													background: "transparent",
													opacity: 0,
													width: "100%",
													height: "100%",
												}}
												disabled={disabled}
												onChange={(e) => {
													const value = e.currentTarget.value
													onFiltersChange?.(
														filters.map((f, i) => ({
															...f,
															// update the name to selected value
															name: i === index ? value : f.name,
															// always set included to 1
															included: i === index ? (1 as 0 | 1) : f.included,
														}))
													)
												}}
											>
												{filter.options.map((option) => (
													<option
														value={option.name}
														key={option.name}
														disabled={option.disabled}
													>
														{option.name}
													</option>
												))}
											</select>
										</Box>
									</Box>
								) : (
									<label htmlFor={`${filterId}_checkbox`}>{filter.name}</label>
								)}
							</Inline>
						)
					})}
				</Inline>
			</Stack>
		</Box>
	)
}

function TripInvoiceFormContent({
	trip,
	payload,
	filters,
	setFilters,
	hideTaxBreakup,
	setHideTaxBreakup,
	revalidateTrip,
	revalidateInvoice,
	confirm,
	...props
}: React.ComponentProps<typeof TripInvoiceForm> &
	Omit<
		React.ComponentProps<typeof InvoiceForm>,
		| "currency"
		| "sellerFunctionalCurrency"
		| "initialValues"
		| "onFormReady"
		| "filters"
		| "hideTaxBreakup"
	> & {
		payload: Required<
			InvoiceFormData & TTemplateProformaInvoiceData,
			"currency" | "items" | "total_amount"
		> & { hide_tax_breakup?: 0 | 1 }
		confirm?: () => Promise<boolean>
		revalidateInvoice: () => void
	}) {
	const [editingBuyer, setEditBuyer] = useState(false)
	const resolveRef = useRef<
		undefined | ((value: { address?: IAddress }) => unknown)
	>(undefined)

	const confirmableInvoiceSetFilters = useConfirmableFilterChange<
		Array<TInvoiceFilter>
	>(setFilters, true, confirm)

	const confirmableSetHideTaxBreakup = useConfirmableFilterChange<0 | 1>(
		setHideTaxBreakup,
		true,
		confirm
	)

	//Trip Invoice Custom filters configuration
	const invoiceCustomFilters: Array<TInvoiceCustomFilter> = useMemo(
		() => [
			{
				id: "hide_tax_breakup",
				name: "Hide Tax Breakup",
				included: hideTaxBreakup || 0,
				onChange: confirmableSetHideTaxBreakup,
			},
		],
		[hideTaxBreakup, confirmableSetHideTaxBreakup]
	)

	const {
		default_hsn,
		display_hsn,
		display_place_of_supply,
		invoice_title,
		items,
		...rest
	} = payload

	// Transform items to derive tax_applied_on_percentage from editing_amount as "tax_applied_on_percentage" flag is not provided by the api for each item.
	// InvoiceForm maintains "tax_applied_on_percentage" flag at root of each item as single source of truth for all taxes in a single item, as this setting is global for all taxes inside an item
	// which can be toggle from the tax table header inside an item as because this key is not provided by the backend api.
	const transformedItems = useMemo(() => {
		return (items || []).map((item) => {
			// Derive tax_applied_on_percentage from first tax item's editing_amount flag
			const firstTax = item.taxes?.[0]
			const taxAppliedOnPercentage =
				firstTax?.editing_amount !== undefined &&
				firstTax?.editing_amount !== null
					? firstTax.editing_amount === 0
						? (1 as const)
						: (0 as const)
					: (1 as const) // default to percentage mode

			return {
				...item,
				tax_applied_on_percentage: taxAppliedOnPercentage,
			}
		})
	}, [items])

	const initialValues = useMemo(
		() => ({
			...rest,
			items: transformedItems,
			expected_total_amount: trip.package_amount,
		}),
		[transformedItems, rest, trip.package_amount]
	)

	const handleEditBuyerAddress = useCallback(() => {
		setEditBuyer(true)
		return new Promise<{ address?: IAddress }>((resolve) => {
			resolveRef.current = resolve
		})
	}, [setEditBuyer])

	const templateInvoiceData = useMemo(
		() => ({
			default_hsn: default_hsn || null,
			display_hsn: display_hsn || false,
			display_place_of_supply: display_place_of_supply || false,
			invoice_title: invoice_title || "",
		}),
		[default_hsn, display_hsn, display_place_of_supply, invoice_title]
	)

	return (
		<>
			<Stack gap="2">
				{/* Combined Filters: Both custom and dyamic */}
				{/* Only show filters when setFilters is provided (i.e., during creation, not editing) */}
				{setFilters && (filters || invoiceCustomFilters) ? (
					<InvoiceFormFilters
						filters={filters}
						onFiltersChange={confirmableInvoiceSetFilters}
						customFilters={invoiceCustomFilters}
					/>
				) : null}
				<InvoiceForm
					{...props}
					sellerFunctionalCurrency={payload.seller_functional_currency}
					currency={payload.currency}
					hideTaxBreakup={hideTaxBreakup}
					initialValues={initialValues}
					onEditBuyerAddress={handleEditBuyerAddress}
					templateInvoiceData={templateInvoiceData}
				/>
			</Stack>

			<Dialog
				open={editingBuyer}
				onClose={() => setEditBuyer(false)}
				title={
					trip.trip_source.is_agent
						? "Edit B2B Source Details"
						: "Edit Guest Details"
				}
				lg
			>
				<Dialog.Body>
					{!trip.trip_source.is_agent && (trip.tourists || []).length ? (
						<UpdateTourist
							tripId={trip.id}
							tripStartDateLocal={trip.start_date_local}
							onCancel={() => setEditBuyer(false)}
							onSuccess={(tourist) => {
								resolveRef.current?.(tourist)
								revalidateTrip()
								revalidateInvoice()
								setEditBuyer(false)
							}}
						/>
					) : (
						<EditTripSourceItem
							tripSourceId={trip.trip_source.id}
							onCancel={() => setEditBuyer(false)}
							hideTypeToggle
							onSuccess={(source) => {
								resolveRef.current?.(source)
								revalidateTrip()
								setEditBuyer(false)
							}}
						/>
					)}
				</Dialog.Body>
			</Dialog>

			<ConfirmationDialog
				title="Unsaved Changes"
				acceptActionLabel="Yes, continue"
				rejectActionLabel="Cancel"
				destructiveAction={true}
			>
				<Text>
					You have unsaved changes in the invoice form. Changing filters will
					reset the form and you'll lose your current progress. Are you sure you
					want to continue?
				</Text>
			</ConfirmationDialog>
		</>
	)
}

function TripInvoiceForm({
	trip,
	fetchPayload,
	revalidateTrip,
	filters,
	setFilters,
	hideTaxBreakup,
	setHideTaxBreakup,
	...props
}: Omit<
	React.ComponentProps<typeof InvoiceForm>,
	"currency" | "sellerFunctionalCurrency" | "initialValues"
> & {
	trip: Pick<
		ITrip,
		| "id"
		| "trip_source"
		| "tourist"
		| "package_amount"
		| "tourists"
		| "start_date_local"
	>
	fetchPayload: (
		xhr: XHRInstance,
		params: unknown
	) => Promise<
		Required<
			InvoiceFormData & TTemplateProformaInvoiceData,
			"currency" | "items" | "total_amount"
		> & { options?: Array<TInvoiceFilter>; hide_tax_breakup?: 0 | 1 }
	>
	revalidateTrip: () => void
	filters?: Array<TInvoiceFilter>
	setFilters?: (filters: Array<TInvoiceFilter>) => void
	hideTaxBreakup?: 0 | 1
	setHideTaxBreakup?: (value: 0 | 1) => void
}) {
	const xhr = useXHR()
	const id = useId()

	//flag: has user interacted with filters
	const userHasChangedFilters = useRef(false)

	// Build filter params only when user has changed filters
	const getFilterParams = useCallback(() => {
		if (!userHasChangedFilters.current) {
			// User hasn't changed filters yet, don't send any filters to API
			return { inclusions: null, exclusions: null }
		}

		// User has changed filters, send them to API
		const allFilters = [
			...(filters || []),
			...(hideTaxBreakup !== undefined
				? [
						{
							id: "hide_tax_breakup",
							name: "Hide Tax Breakup",
							included: hideTaxBreakup,
						},
					]
				: []),
		]

		return {
			inclusions:
				allFilters
					.filter((f) => f.included)
					.map((f) => f.name)
					.join(",") || null,
			exclusions:
				allFilters
					.filter((f) => !f.included)
					.map((f) => f.name)
					.join(",") || null,
		}
	}, [filters, hideTaxBreakup])

	const {
		data: payload,
		isValidating,
		mutate: revalidateInvoice,
	} = useSWR(
		`/api/trips/${trip.id}/invoices/form?${id}&filters=${JSON.stringify(getFilterParams())}`,
		() =>
			fetchPayload(xhr, {
				timezone_offset: config.timezoneOffset,
				...getFilterParams(),
			}).then((payload) => {
				if (payload.options) {
					setFilters?.(payload.options)
				}

				if (payload.hide_tax_breakup !== undefined) {
					setHideTaxBreakup?.(payload.hide_tax_breakup)
				}

				const { options, ...rest } = payload

				return { ...rest, issued_at: new Date() }
			})
	)

	//Wrap filter setters to mark when USER changes filters (vs API initialization).
	//This prevents re-triggering API calls when filters are initialized from API response.
	//Only when user interacts with filters should new API calls be made, helps to avoids circular dependency.
	const wrappedSetFilters = useCallback(
		(newFilters: Array<TInvoiceFilter>) => {
			userHasChangedFilters.current = true
			setFilters?.(newFilters)
		},
		[setFilters]
	)

	//Wrap filter setters to mark when USER changes filters (vs API initialization).
	//This prevents re-triggering API calls when filters are initialized from API response.
	//Only when user interacts with filters should new API calls be made, helps to avoids circular dependency.
	const wrappedSetHideTaxBreakup = useCallback(
		(newValue: 0 | 1) => {
			userHasChangedFilters.current = true
			setHideTaxBreakup?.(newValue)
		},
		[setHideTaxBreakup]
	)

	if (!payload || isValidating) {
		return <Spinner padding="4" alignCenter />
	}

	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<TripInvoiceFormContent
					{...props}
					fetchPayload={fetchPayload}
					filters={filters}
					setFilters={setFilters ? wrappedSetFilters : undefined}
					hideTaxBreakup={hideTaxBreakup}
					setHideTaxBreakup={wrappedSetHideTaxBreakup}
					payload={payload}
					trip={trip}
					revalidateTrip={revalidateTrip}
					revalidateInvoice={revalidateInvoice}
					confirm={confirm}
				/>
			)}
		</ConfirmationDialogProvider>
	)
}

/**
 * Hook that wraps filter changes with confirmation dialog to show confirmation dialog based on "isDirty" prop
 */

function useConfirmableFilterChange<T>(
	onFiltersChange?: (filters: T) => void,
	isDirty?: boolean,
	confirm?: () => Promise<boolean>
) {
	return useCallback(
		async (newFilters: T) => {
			if (isDirty && confirm) {
				const shouldContinue = await confirm()
				if (!shouldContinue) {
					return // User cancelled, don't apply filter changes
				}
			}

			onFiltersChange?.(newFilters)
		},
		[onFiltersChange, isDirty, confirm]
	)
}
